import React, { useState } from 'react';
import { Form, Input, Button, Alert, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { API_ORIGIN } from '../config';
import AuthLayout from '../components/AuthLayout';

const API_BASE_URL = `${API_ORIGIN}/api`;

const ForgotPassword = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSendResetCode = async (values) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to send reset code';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      await response.json();
      setEmail(values.email);
      setEmailSent(true);
      setCurrentStep(1);
      message.success('Reset code sent! Please check your email.');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: values.code, newPassword: values.password }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to reset password';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      await response.json();
      message.success('Password reset successfully! Please login with your new password.');
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = () => {
    setCurrentStep(0);
    setEmailSent(false);
    setError('');
  };

  const renderStep1 = () => (
    <Form name="forgotPassword" onFinish={handleSendResetCode} layout="vertical" requiredMark={false}>
      <Form.Item
        name="email"
        label="Email address"
        rules={[
          { required: true, message: 'Enter your email' },
          { type: 'email', message: 'Enter a valid email address' },
        ]}
        style={{ marginBottom: 28 }}
      >
        <Input type="email" placeholder="you@festivals.mt" autoComplete="username" autoFocus />
      </Form.Item>

      <Button type="primary" htmlType="submit" loading={loading} className="auth__submit">
        Send reset code
      </Button>
    </Form>
  );

  const renderStep2 = () => (
    <Form name="resetPassword" onFinish={handleResetPassword} layout="vertical" requiredMark={false}>
      {emailSent && (
        <Alert
          type="info"
          showIcon
          message="Check your email"
          description={
            <span>
              We sent a 6-digit code to <strong>{email}</strong>
            </span>
          }
        />
      )}

      <Form.Item
        name="code"
        label="Reset code"
        rules={[
          { required: true, message: 'Enter the reset code' },
          { len: 6, message: 'The reset code is 6 digits' },
        ]}
      >
        <Input className="auth__code" placeholder="000000" maxLength={6} inputMode="numeric" autoComplete="one-time-code" autoFocus />
      </Form.Item>

      <Form.Item
        name="password"
        label="New password"
        rules={[
          { required: true, message: 'Enter a new password' },
          { min: 8, message: 'Use at least 8 characters' },
        ]}
      >
        <Input.Password placeholder="Create a new password" autoComplete="new-password" />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label="Confirm password"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Confirm your password' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) return Promise.resolve();
              return Promise.reject(new Error('Passwords do not match'));
            },
          }),
        ]}
        style={{ marginBottom: 28 }}
      >
        <Input.Password placeholder="Repeat your password" autoComplete="new-password" />
      </Form.Item>

      <div className="auth__aux">
        <button type="button" className="auth__link" onClick={resendCode}>
          Didn't get a code? Send it again
        </button>
      </div>

      <Button type="primary" htmlType="submit" loading={loading} className="auth__submit">
        Reset password
      </Button>
    </Form>
  );

  return (
    <AuthLayout
      title={currentStep === 0 ? 'Forgot your password?' : 'Choose a new password.'}
      subtitle={
        currentStep === 0 ? (
          <>We'll email you a code to reset your <strong>Festivals Portal</strong> password.</>
        ) : (
          'Enter the code from your email and pick a new password.'
        )
      }
      footer={
        <>
          Remembered it? <Link to="/login" className="auth__link">Back to sign in</Link>
        </>
      }
    >
      {error && (
        <Alert message={error} type="error" showIcon closable onClose={() => setError('')} role="alert" />
      )}

      {currentStep === 0 ? renderStep1() : renderStep2()}
    </AuthLayout>
  );
};

export default ForgotPassword;
